import DoneIcon from '@mui/icons-material/Done'
import { Box, Button, Checkbox, FormControlLabel, Switch } from '@mui/material'
import { useContext, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { HelpText } from 'genesis-suite/components'
import { Dashboard } from 'genesis-suite/types/visualTypes'
import { navigationCreators } from '~/actions/creators'
import NewWidgetButton from '~/components/NewWidgetButton'
import useHomeDashboard, { useClearAllHomeDashboards } from '~/hooks/useHomeDashboard'
import defaultDashboard from '~/lib/defaultHomePage'
import { routePaths } from '~/lib/routes'
import { visualService } from '~/lib/services'
import { applicationSelectors, deploymentSelectors, moduleSelectors } from '~/selectors'
import { DashboardContext } from './DashboardContext'
import RestoreHiddenWidgets from './RestoreHiddenWidgets'
import { DashboardDevicePreviewToggle } from './actions'

export default function HomePageHeader() {
    const appName = useSelector(applicationSelectors.getCurrentAppName)
    const moduleId = useSelector(moduleSelectors.getModuleId)
    const isV2 = useSelector(moduleSelectors.getIsV2)
    const viewFlag = useSelector(deploymentSelectors.getDeploymentViewFlag)
    const dispatch = useDispatch()

    const { dashboard, isLoading } = useHomeDashboard()
    const clearAllHomeDashboards = useClearAllHomeDashboards()
    const { editing, onEditDone, onEditStart } = useContext(DashboardContext)

    useEffect(() => {
        if (dashboard && !editing) onEditStart()
    }, [dashboard, editing])

    if (isLoading) return null

    const type = getType(dashboard)

    async function handleDone() {
        if (editing) await onEditDone()
        dispatch(navigationCreators.goTo(routePaths.HOME))
    }

    async function handleScopeChange() {
        let updated: Dashboard

        switch (type) {
            case 'default':
                const moduleDashboard = { ...defaultDashboard, ...(isV2 ? { moduleId } : { visorModuleId: moduleId }) }
                updated = await visualService.createDashboard(appName, moduleDashboard, viewFlag)
                break
            case 'all-modules':
                updated = await visualService.cloneDashboard(
                    appName,
                    dashboard.id,
                    isV2 ? { moduleId } : { visorModuleId: moduleId },
                    viewFlag
                )
                break
            case 'single-module':
                await visualService.deleteDashboard(appName, dashboard?.id)
                break
        }

        clearAllHomeDashboards()
    }

    async function handleCustomChange() {
        let updated: Dashboard

        switch (type) {
            case 'default':
                updated = await visualService.createDashboard(appName, defaultDashboard, viewFlag)
                break
            case 'all-modules':
                await visualService.deleteDashboard(appName, dashboard?.id)
                break
        }

        clearAllHomeDashboards()
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1,
                px: 2,
                borderColor: 'text.primary',
                borderBottom: 1,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <HelpText help="Changes made to the home view affect all users" variant="h6">
                    Manage Home View
                </HelpText>

                <FormControlLabel
                    checked={type === 'single-module'}
                    control={<Switch color="primary" />}
                    label={
                        <HelpText help="Users will see this home view for this application only">
                            This application only
                        </HelpText>
                    }
                    labelPlacement="end"
                    onChange={handleScopeChange}
                />
                <FormControlLabel
                    control={<Checkbox checked={type !== 'default'} onChange={handleCustomChange} />}
                    disabled={type === 'single-module'}
                    label={<HelpText help="Design a custom layout">Custom</HelpText>}
                />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {type !== 'default' && (
                    <>
                        <RestoreHiddenWidgets />
                        <NewWidgetButton />
                        <DashboardDevicePreviewToggle />
                    </>
                )}
                <Button onClick={handleDone} startIcon={<DoneIcon />} variant="contained">
                    Done
                </Button>
            </Box>
        </Box>
    )
}

type HomePageType = 'default' | 'all-modules' | 'single-module'

function getType(dashboard?: Dashboard): HomePageType {
    if (!dashboard) return 'default'
    if (dashboard.moduleId || dashboard.visorModuleId) return 'single-module'
    return 'all-modules'
}
